/**
 * Navigation Instructions Generator
 * 
 * Generates dynamic turn-by-turn navigation instructions from route coordinates
 * and current user position.
 */

export interface NavigationInstruction {
  instruction: string; // Translation key or instruction text
  distance: number; // Distance to this instruction in meters
  type: 'turn-left' | 'turn-right' | 'straight' | 'arrive' | 'start';
  bearing?: number; // Direction in degrees (0-360)
}

export interface RoutePoint {
  lat: number;
  lng: number;
  distanceFromStart: number; // Cumulative distance from start
}

/**
 * Calculate distance between two points using Haversine formula
 */
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

/**
 * Calculate bearing (direction) from point A to point B
 * Returns degrees (0-360), where 0 is North
 */
function calculateBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;
  
  const y = Math.sin(dLng) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - 
            Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
  
  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360; // Normalize to 0-360
}

/**
 * Calculate angle difference between two bearings
 */
function angleDifference(bearing1: number, bearing2: number): number {
  let diff = bearing2 - bearing1;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return diff;
}

/**
 * Find the closest point on the route to the user's current position
 */
function findClosestPointOnRoute(
  userLat: number,
  userLng: number,
  routeCoordinates: [number, number][]
): { index: number; distance: number } {
  let minDistance = Infinity;
  let closestIndex = 0;

  for (let i = 0; i < routeCoordinates.length; i++) {
    const [lat, lng] = routeCoordinates[i];
    const distance = calculateDistance(userLat, userLng, lat, lng);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = i;
    }
  }

  return { index: closestIndex, distance: minDistance };
}

/**
 * Generate navigation instructions from route coordinates
 */
export function generateNavigationInstructions(
  routeCoordinates: [number, number][],
  currentPosition: [number, number] | null,
  endPosition: [number, number]
): NavigationInstruction[] {
  if (routeCoordinates.length < 2) {
    return [];
  }

  const instructions: NavigationInstruction[] = [];
  
  // Calculate cumulative distances and bearings for each point
  const routePoints: RoutePoint[] = [];
  let cumulativeDistance = 0;

  for (let i = 0; i < routeCoordinates.length; i++) {
    const [lat, lng] = routeCoordinates[i];
    
    if (i > 0) {
      const [prevLat, prevLng] = routeCoordinates[i - 1];
      cumulativeDistance += calculateDistance(prevLat, prevLng, lat, lng);
    }
    
    routePoints.push({
      lat,
      lng,
      distanceFromStart: cumulativeDistance
    });
  }

  // Find user's position along the route
  let currentIndex = 0;
  if (currentPosition) {
    const closest = findClosestPointOnRoute(
      currentPosition[0],
      currentPosition[1],
      routeCoordinates
    );
    currentIndex = closest.index;
  }

  // Generate instructions by analyzing route geometry
  // Look for significant direction changes (turns)
  const TURN_THRESHOLD = 25; // Minimum angle change in degrees to consider it a turn
  const MIN_DISTANCE_BETWEEN_TURNS = 15; // Minimum distance in meters between turns
  const SMOOTHING_WINDOW = 3; // Number of points to average for smoother bearing calculation

  let lastInstructionIndex = currentIndex;
  let lastBearing: number | null = null;

  // Start instruction
  if (currentIndex === 0 && routePoints.length > 1) {
    instructions.push({
      instruction: 'start',
      distance: 0,
      type: 'start',
      bearing: calculateBearing(
        routePoints[0].lat, routePoints[0].lng,
        routePoints[Math.min(5, routePoints.length - 1)].lat, 
        routePoints[Math.min(5, routePoints.length - 1)].lng
      )
    });
  }

  // Analyze route ahead for turns
  // Use a sliding window to smooth out bearing calculations
  for (let i = Math.max(currentIndex + 1, 2); i < routePoints.length - 2; i++) {
    const point = routePoints[i];
    const nextPoint = routePoints[Math.min(i + SMOOTHING_WINDOW, routePoints.length - 1)];
    const prevPoint = routePoints[Math.max(i - SMOOTHING_WINDOW, 0)];

    // Calculate smoothed bearings
    const bearingToNext = calculateBearing(point.lat, point.lng, nextPoint.lat, nextPoint.lng);
    const bearingFromPrev = calculateBearing(prevPoint.lat, prevPoint.lng, point.lat, point.lng);

    if (lastBearing === null) {
      lastBearing = bearingFromPrev;
    }

    // Check for significant direction change
    const angleChange = angleDifference(lastBearing, bearingToNext);
    const distanceFromLastInstruction = point.distanceFromStart - routePoints[lastInstructionIndex].distanceFromStart;

    if (Math.abs(angleChange) > TURN_THRESHOLD && distanceFromLastInstruction > MIN_DISTANCE_BETWEEN_TURNS) {
      // Determine turn type
      let turnType: 'turn-left' | 'turn-right' | 'straight' = 'straight';
      if (angleChange > TURN_THRESHOLD) {
        turnType = 'turn-right';
      } else if (angleChange < -TURN_THRESHOLD) {
        turnType = 'turn-left';
      }

      // Calculate distance from current position
      const distanceFromCurrent = currentPosition
        ? calculateDistance(
            currentPosition[0], currentPosition[1],
            point.lat, point.lng
          )
        : point.distanceFromStart - routePoints[currentIndex].distanceFromStart;

      // Only add instruction if it's ahead of the user
      if (distanceFromCurrent > 0) {
        instructions.push({
          instruction: turnType === 'turn-left' ? 'turnLeft' : 
                      turnType === 'turn-right' ? 'turnRight' : 
                      'goStraight',
          distance: Math.round(distanceFromCurrent),
          type: turnType,
          bearing: bearingToNext
        });

        lastInstructionIndex = i;
        lastBearing = bearingToNext;
      }
    } else {
      lastBearing = bearingToNext;
    }
  }

  // Add arrival instruction
  const endDistance = currentPosition
    ? calculateDistance(
        currentPosition[0], currentPosition[1],
        endPosition[0], endPosition[1]
      )
    : routePoints[routePoints.length - 1].distanceFromStart - routePoints[currentIndex].distanceFromStart;

  instructions.push({
    instruction: 'arrived',
    distance: Math.max(0, Math.round(endDistance)),
    type: 'arrive'
  });

  return instructions;
}

/**
 * Get the current instruction based on user position
 */
export function getCurrentInstruction(
  instructions: NavigationInstruction[],
  currentPosition: [number, number] | null,
  endPosition: [number, number]
): NavigationInstruction | null {
  if (instructions.length === 0) {
    return null;
  }

  // If we're very close to destination (< 50m), show arrival
  if (currentPosition) {
    const distanceToEnd = calculateDistance(
      currentPosition[0], currentPosition[1],
      endPosition[0], endPosition[1]
    );
    
    if (distanceToEnd < 50) {
      return instructions[instructions.length - 1]; // Return arrival instruction
    }
  }

  // Find the next instruction that hasn't been passed:
  // pick the non-arrival instruction with the smallest positive distance.
  const nonArrivalInstructions = instructions.filter(
    (instr) => instr.type !== 'arrive'
  );

  let nextInstruction: NavigationInstruction | null = null;
  for (const instruction of nonArrivalInstructions) {
    if (instruction.distance > 0) {
      if (!nextInstruction || instruction.distance < nextInstruction.distance) {
        nextInstruction = instruction;
      }
    }
  }

  // If we found a valid upcoming instruction, use it.
  if (nextInstruction) {
    return nextInstruction;
  }

  // If we have no non-arrival instructions but we do have a current position
  // and we're still far from the destination, synthesize a "go straight"
  // instruction instead of showing arrival.
  if (currentPosition) {
    const distanceToEnd = calculateDistance(
      currentPosition[0],
      currentPosition[1],
      endPosition[0],
      endPosition[1]
    );

    if (distanceToEnd >= 50) {
      return {
        instruction: 'goStraight',
        distance: Math.round(distanceToEnd),
        type: 'straight',
      };
    }
  }

  // Fallback: if truly nothing else applies, return the first instruction
  // (may be "start" or "arrive").
  return instructions[0];
}

/**
 * Get direction name from bearing (for display)
 */
export function getDirectionFromBearing(bearing: number): string {
  const directions = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
  const index = Math.round(bearing / 45) % 8;
  return directions[index];
}
